import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiChevronLeft, FiChevronRight, FiX } = FiIcons;

const DatePicker = ({ 
  startDate, 
  endDate, 
  onDateChange, 
  showPresets = true,
  presets = {},
  className = "" 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('calendar'); // 'presets' or 'calendar'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedRange, setSelectedRange] = useState({ start: startDate, end: endDate });
  const [hoverDate, setHoverDate] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDateDisplay = (start, end) => {
    if (!start || !end) return 'เลือกช่วงวันที่';
    
    const startStr = start.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    
    const endStr = end.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    
    return `${startStr} - ${endStr}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const isDateInRange = (date) => {
    if (!selectedRange.start || !selectedRange.end || !date) return false;
    return date >= selectedRange.start && date <= selectedRange.end;
  };

  const isDateSelected = (date) => {
    if (!date) return false;
    return (selectedRange.start && date.getTime() === selectedRange.start.getTime()) ||
           (selectedRange.end && date.getTime() === selectedRange.end.getTime());
  };

  const isDateInHoverRange = (date) => {
    if (!selectedRange.start || !hoverDate || !date) return false;
    const start = selectedRange.start;
    const end = hoverDate;
    const minDate = start < end ? start : end;
    const maxDate = start < end ? end : start;
    return date >= minDate && date <= maxDate;
  };

  const handleDateClick = (date) => {
    if (!selectedRange.start || (selectedRange.start && selectedRange.end)) {
      // Start new selection
      setSelectedRange({ start: date, end: null });
    } else {
      // Complete selection
      const start = selectedRange.start;
      const end = date;
      const finalStart = start < end ? start : end;
      const finalEnd = start < end ? end : start;
      
      setSelectedRange({ start: finalStart, end: finalEnd });
      onDateChange(finalStart, finalEnd);
      setIsOpen(false);
    }
  };

  const handlePresetClick = (preset) => {
    const { startDate: presetStart, endDate: presetEnd } = preset;
    setSelectedRange({ start: presetStart, end: presetEnd });
    onDateChange(presetStart, presetEnd);
    setIsOpen(false);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const days = getDaysInMonth(currentMonth);
  const monthYear = currentMonth.toLocaleDateString('th-TH', {
    month: 'long',
    year: 'numeric'
  });

  const dayNames = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors ${
          (startDate && endDate) 
            ? 'border-blue-300 bg-blue-50 text-blue-700' 
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        }`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <SafeIcon icon={FiCalendar} className="h-4 w-4" />
        <span className="text-sm">
          {formatDateDisplay(startDate, endDate)}
        </span>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div className="w-96">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h4 className="text-sm font-medium text-gray-900">เลือกช่วงวันที่</h4>
                <div className="flex items-center space-x-2">
                  {showPresets && (
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setView('presets')}
                        className={`px-3 py-1 text-xs rounded-md transition-colors ${
                          view === 'presets' 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        ช่วงที่กำหนด
                      </button>
                      <button
                        onClick={() => setView('calendar')}
                        className={`px-3 py-1 text-xs rounded-md transition-colors ${
                          view === 'calendar' 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        ปฏิทิน
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <SafeIcon icon={FiX} className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                {view === 'presets' && showPresets ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {Object.entries(presets).map(([key, preset]) => (
                      <button
                        key={key}
                        onClick={() => handlePresetClick(preset)}
                        className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="font-medium">{preset.label}</div>
                        <div className="text-xs text-gray-500">
                          {formatDateDisplay(preset.startDate, preset.endDate)}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div>
                    {/* Calendar Header */}
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={() => navigateMonth(-1)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <SafeIcon icon={FiChevronLeft} className="h-4 w-4" />
                      </button>
                      
                      <h5 className="text-sm font-medium text-gray-900">{monthYear}</h5>
                      
                      <button
                        onClick={() => navigateMonth(1)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <SafeIcon icon={FiChevronRight} className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Day Names */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {dayNames.map(day => (
                        <div key={day} className="text-xs font-medium text-gray-500 text-center p-2">
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="grid grid-cols-7 gap-1">
                      {days.map((date, index) => (
                        <button
                          key={index}
                          onClick={() => date && handleDateClick(date)}
                          onMouseEnter={() => date && setHoverDate(date)}
                          onMouseLeave={() => setHoverDate(null)}
                          disabled={!date}
                          className={`
                            h-8 text-xs rounded transition-colors
                            ${!date ? 'invisible' : ''}
                            ${isDateSelected(date) 
                              ? 'bg-blue-600 text-white' 
                              : isDateInRange(date) || isDateInHoverRange(date)
                                ? 'bg-blue-100 text-blue-700'
                                : 'hover:bg-gray-100 text-gray-700'
                            }
                          `}
                        >
                          {date?.getDate()}
                        </button>
                      ))}
                    </div>

                    {/* Selection Info */}
                    {selectedRange.start && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <div className="text-xs text-gray-600">
                          {selectedRange.start && selectedRange.end ? (
                            <>
                              <span className="font-medium">ช่วงที่เลือก:</span><br />
                              {formatDateDisplay(selectedRange.start, selectedRange.end)}
                            </>
                          ) : (
                            <>
                              <span className="font-medium">วันเริ่มต้น:</span><br />
                              {selectedRange.start.toLocaleDateString('th-TH', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}<br />
                              <span className="text-blue-600">เลือกวันสิ้นสุด</span>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DatePicker;